import { z } from "zod";
import {
  CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME,
  CustomAttributes,
  generateDynamicZodSchema,
} from "./generateCustomAttributesSchema";
import { progressUpdateAttachmentSchema, zod } from "./schemas";

/** The longest name suffix the service accepts (Smithy `suffix`, @length max 50). */
export const MAX_TITLE_SUFFIX_LENGTH = 50;

/*
 * Zod gaiaObject UI schema
 */
const gaiaObjFormSchemaForUIVisible = z.object({
  titleSuffix: z
    .string()
    .max(
      MAX_TITLE_SUFFIX_LENGTH,
      `Name suffix limited to ${MAX_TITLE_SUFFIX_LENGTH} characters`,
    )
    .optional(),
  contactIds: z
    .array(z.string())
    .max(10, "You can select a maximum of 10 external authority contacts")
    .optional(),
  pncEnabled: z.boolean().optional(),
  pncUsers: z.array(z.string()).optional(),
});

/*
 * This is the schema for the attributes, which are not shown in UI, are not part of the CreateGaiaObject schema, but are required for gaiaObject object sent to backend
 */
const gaiaObjFormSchemaForUIInVisible = z.object({
  gaiaObjectId: z.string(),
  shortId: z.string(),
  teamName: zod.string(),
  category: zod.string(),
  title: zod.string(),
  customAttributesDataMap: z.record(z.string(), z.string()).optional(),
  progressUpdateDocIds: z.array(z.string()).optional(),
  progressUpdateAttachments: z.array(progressUpdateAttachmentSchema).optional(),
});

/*
 * This is for generating the gaiaObject dynamic schemas used for both creating/editing gaiaObject form validation, and data object schema for saving to backend
 */
export const generateGaiaObjDynamicSchemas = (
  gaiaObjCustomAttrData: CustomAttributes,
) => {
  const gaiaObjCustomAttrSchema = generateDynamicZodSchema(
    gaiaObjCustomAttrData,
  );

  const gaiaObjFormSchemaWithoutCrossRefinement = z.object({
    ...gaiaObjFormSchemaForUIVisible.shape,
    [CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME]: gaiaObjCustomAttrSchema.optional(),
  });

  // This is the zod schema for creating/editing gaiaObject form both required attributes and custom attributes
  const gaiaObjFormSchema = gaiaObjFormSchemaWithoutCrossRefinement.refine(
    (data) => {
      const attrMap = data?.[CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME];
      const start = attrMap?.periodStartDate;
      const end = attrMap?.periodEndDate;

      if (start && end) {
        return end > start;
      }

      return true;
    },
    {
      message: "Peroid end date should be after peroid start date",
      path: [CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME, "periodEndDate"],
    },
  );

  // This is the zod schema for the entire gaiaObject related attributes built, this is composed of four parts:
  // 1. Required attributes: invisible attributes
  // 2. Required attributes: visible attributes
  // 3. Custom attributes: visible attributes
  // 4. Custom attributes: invisible attributes (they are all string: string value pair for simplicity)
  const gaiaObjSchema = z.object({
    ...gaiaObjFormSchemaForUIInVisible.shape,
    ...gaiaObjFormSchemaWithoutCrossRefinement.shape,
    [CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME]: z
      .record(zod.string(), zod.string())
      .optional(),
  });

  return {
    gaiaObjSchema,
    gaiaObjFormSchema,
  };
};

const basicGaiaObjFormSchemaWithoutCrossRefinement = z.object({
  ...gaiaObjFormSchemaForUIVisible.shape,
});

export const basicGaiaObjFormSchema =
  basicGaiaObjFormSchemaWithoutCrossRefinement;

export const basicGaiaObjSchema = z.object({
  ...gaiaObjFormSchemaForUIInVisible.shape,
  ...basicGaiaObjFormSchemaWithoutCrossRefinement.shape,
});

export type GaiaObject = z.infer<typeof basicGaiaObjSchema>;
